import type { Scenario, ActionItem } from "./types";
import approvalSeeds from "./data/approval-costs-nsw.json";
import balDefaults from "./data/bal-defaults.json";
import hazmatDefaults from "./data/hazmat-defaults.json";

export function generateActionItems(scenario: Scenario): ActionItem[] {
  const items: ActionItem[] = [];
  const a = scenario.assumptions;

  // ---- External works underscoping ----
  const externalWorksActual = a.externalWorksPct ?? 0.15;
  if (externalWorksActual < 0.12) {
    items.push({
      id: "external_works_underscoping",
      severity: "high",
      title: "External works allowance may be underscoped",
      description: `Greenfield-school benchmark: 12-18% of TPC for landscaping, hardstand, and services. Current assumption: ${(externalWorksActual * 100).toFixed(1)}%. Adaptive-reuse sites may need more external intervention than greenfield.`,
      estimatedCostBandLow:
        scenario.buildings.reduce((s, b) => s + b.gfaSqm, 0) * 150,
      estimatedCostBandHigh:
        scenario.buildings.reduce((s, b) => s + b.gfaSqm, 0) * 350,
      status: "open",
      source:
        "Education Construction Cost Trends 2021-2026: external works benchmark 12-18%",
    });
  }

  // ---- Approval cost action items (Phase G) ----
  const reddamSharePct = a.approvalCostReddamSharePct ?? 0.5;
  const reddamPct = Math.round(reddamSharePct * 100);
  const devPct = Math.round((1 - reddamSharePct) * 100);

  if (!a.ssdaCost || a.ssdaCost === 0) {
    items.push({
      id: "ssda_unspecified",
      severity: "high",
      title: "SSDA cost not specified",
      description: `NSW State Significant Development Application required for projects with CIV > $30M. Reddam-developer split: ${reddamPct}% Reddam / ${devPct}% developer.`,
      estimatedCostBandLow: approvalSeeds.ssdaCostBand.low,
      estimatedCostBandHigh: approvalSeeds.ssdaCostBand.high,
      status: "open",
      source: "NSW EP&A Act + Reddam-developer pre-agreed deal terms",
    });
  }
  if (!a.changeOfUseCost || a.changeOfUseCost === 0) {
    items.push({
      id: "change_of_use_unspecified",
      severity: "med",
      title: "Change-of-use approval cost not specified",
      description: `Change of use from commercial to school (Class 9b) under NSW EP&A Act + SEPP Education 2023. Reddam-developer split: ${reddamPct}% / ${devPct}%.`,
      estimatedCostBandLow: approvalSeeds.changeOfUseCostBand.low,
      estimatedCostBandHigh: approvalSeeds.changeOfUseCostBand.high,
      status: "open",
      source: "NSW EP&A Act + SEPP Education 2023",
    });
  }
  if (!a.otherApprovalCosts || a.otherApprovalCosts === 0) {
    items.push({
      id: "other_approvals_unspecified",
      severity: "med",
      title: "Other approval costs not specified (BAL cert, ESD, traffic)",
      description: `Aggregate of BAL certification, ESD report, traffic study, and other minor approvals. Reddam-developer split per scenario.`,
      estimatedCostBandLow: approvalSeeds.otherApprovalCostBand.low,
      estimatedCostBandHigh: approvalSeeds.otherApprovalCostBand.high,
      status: "open",
      source: "NSW typical approval workstack",
    });
  }

  // ---- Per-building BAL ----
  for (const b of scenario.buildings) {
    const rating = b.balRating ?? "unknown";
    const band = (
      balDefaults.bands as Record<
        string,
        { low: number; high: number; description: string }
      >
    )[rating];
    if (band && band.high > 0 && rating !== "LOW") {
      items.push({
        id: `bal_${b.id}`,
        severity:
          rating === "BAL-40"
            ? "critical"
            : rating === "BAL-29"
              ? "high"
              : "med",
        title: `BAL ${rating} — ${b.name}`,
        description: band.description,
        estimatedCostBandLow: b.balCostOverride ?? band.low,
        estimatedCostBandHigh: b.balCostOverride ?? band.high,
        affectsBuildings: [b.id],
        status: "open",
        source: "RFS BAL rating cost bands — adaptive-reuse school context",
      });
    }
  }

  // ---- Per-building Hazmat ----
  for (const b of scenario.buildings) {
    const status = b.hazmatStatus ?? "unsurveyed";
    const band = (
      hazmatDefaults.bands as Record<
        string,
        { low: number; high: number; per: string; description: string }
      >
    )[status];
    if (band && band.high > 0) {
      const lowPerSqm = b.hazmatExposurePerSqm ?? band.low;
      const highPerSqm = b.hazmatExposurePerSqm ?? band.high;
      items.push({
        id: `hazmat_${b.id}`,
        severity:
          status === "surveyed-contaminated"
            ? "critical"
            : status === "unsurveyed"
              ? "high"
              : "med",
        title: `Hazmat ${status} — ${b.name}`,
        description: band.description,
        estimatedCostBandLow: lowPerSqm * b.gfaSqm,
        estimatedCostBandHigh: highPerSqm * b.gfaSqm,
        affectsBuildings: [b.id],
        status: "open",
        source: "SafeWork NSW asbestos register requirement",
      });
    }
  }

  // ---- Site deal context ----
  const allocationStatus = a.siteDealAllocationStatus ?? "unverified";
  const fullSitePrice = a.fullSitePrice ?? 113_000_000;
  const campusParcelAllocationLow = a.campusParcelAllocationLow ?? 113_000_000;
  const campusParcelAllocationHigh =
    a.campusParcelAllocationHigh ?? 113_000_000;
  if (
    allocationStatus !== "agreed" &&
    a.purchasePrice >= campusParcelAllocationLow &&
    a.purchasePrice <= campusParcelAllocationHigh
  ) {
    items.push({
      id: "purchase_price_allocation",
      severity: "med",
      title: "Campus-parcel land allocation unverified",
      description: `Whole-site price $${(fullSitePrice / 1e6).toFixed(1)}M. Campus-parcel allocation band: $${(campusParcelAllocationLow / 1e6).toFixed(1)}M – $${(campusParcelAllocationHigh / 1e6).toFixed(1)}M. Current price falls in band but allocation status is "${allocationStatus}".`,
      estimatedCostBandLow: 0,
      estimatedCostBandHigh:
        (campusParcelAllocationHigh - campusParcelAllocationLow) / 2,
      status: "open",
      source: "Whole-site purchase deal structure",
    });
  }

  return items.sort((a, b) => {
    const sevRank = { critical: 0, high: 1, med: 2, low: 3 };
    return sevRank[a.severity] - sevRank[b.severity];
  });
}
